/* Energy-meter maths shared by the Power page and the Dashboard, so "on pace for N
 * units" and the cost figure are computed one way everywhere. */

// Rupees per unit the farm pays the main-meter owner (it is on a sub-meter). ~₹6 is
// what each extra unit adds to their bill: ₹5.00 top slab + ₹0.70 duty + levies,
// from the Jan-Mar 2026 LTIS-I bill. Change it here if the agreed rate changes.
export const UNIT_RATE_INR = 6;

/* /meter/day-profile returns cumulative units since IST midnight per 15-min step:
 * value i is the register's last reading inside step i, so it sits at the END of
 * the step. These helpers put a curve on the clock and read it at any time. */

export const curvePoints = (curve, dayStart, step) => {
  if (!curve) return [];
  // Anchor at 0 at midnight only when the day's readings actually start there.
  const pts = [{ t: dayStart, v: curve[0] != null ? 0 : null }];
  curve.forEach((v, i) => pts.push({ t: dayStart + (i + 1) * step, v }));
  return pts;
};

export const curveAt = (pts, t) => {
  for (let i = 1; i < pts.length; i += 1) {
    if (pts[i].t >= t) {
      const a = pts[i - 1], b = pts[i];
      if (a.v == null || b.v == null) return null;
      return a.v + (b.v - a.v) * (t - a.t) / (b.t - a.t);
    }
  }
  return null;
};

// Average draw implied by a cumulative curve around t, over a one-hour window.
export const drawAt = (pts, t) => {
  const a = curveAt(pts, t - 1800), b = curveAt(pts, t + 1800);
  return a == null || b == null ? null : (b - a) * 1000;
};

/* Today's projected total. First choice is the server's schedule forecast: the lights
 * are most of the load (about 1.4 kW base + 0.67 kW per lit rack, refitted from the
 * last week), so the rest of the day is what the stored light schedules say will be on.
 * It needs no earlier day to resemble today, so it stays right the day after the light
 * hours change, when "what yesterday did" is wrong by several units. Without it: used
 * so far plus what the rest of the day used
 * yesterday (when yesterday was fully recorded). Yesterday comes first because the
 * "typical day" averages the last complete days and lags any change in load: after
 * a new light or AC schedule it projected ~51 units on a day heading for ~60. Then
 * the typical day, then the straight-line rate, which overstates a day whose load is
 * concentrated in daylight. `basis` says which one was used; `fromTypical` is kept
 * for callers that only need "a day-shaped estimate, not the straight line". */
export function todayPace(profile, soFar, now) {
  const dayStart = profile?.day_start;
  if (soFar == null || !dayStart) return { pace: null, fromTypical: false, basis: null };
  const step = profile.step_seconds || 900;
  const restOf = (curve) => {
    const pts = curvePoints(curve, dayStart, step);
    const byNow = curveAt(pts, now);
    const end = curve ? [...curve].reverse().find(v => v != null) : null;
    return byNow != null && end != null ? Math.max(0, end - byNow) : null;
  };
  const fromSchedule = profile.forecast ? restOf(profile.forecast.curve) : null;
  if (fromSchedule != null) return { pace: soFar + fromSchedule, fromTypical: true, basis: 'schedule' };
  const fromYesterday = profile.yesterday_complete ? restOf(profile.yesterday) : null;
  if (fromYesterday != null) return { pace: soFar + fromYesterday, fromTypical: true, basis: 'yesterday' };
  const fromTypical = restOf(profile.typical);
  if (fromTypical != null) return { pace: soFar + fromTypical, fromTypical: true, basis: 'typical' };
  const elapsedH = (now - dayStart) / 3600;
  return { pace: elapsedH >= 2 ? soFar / elapsedH * 24 : null, fromTypical: false, basis: 'rate' };
}
